import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { DbTeamMember } from "@/types/database";
import Icon from "@/components/feature/Icon";
import SmartAvatar from "@/components/feature/SmartAvatar";

type CategoryKey = "all" | "director" | "supervisor" | "advisor" | "secretariat";

const categories: { key: CategoryKey; label: string }[] = [
  { key: "all", label: "全體成員" },
  { key: "director", label: "理事會" },
  { key: "supervisor", label: "監事會" },
  { key: "advisor", label: "顧問團" },
  { key: "secretariat", label: "秘書處" },
];

export default function TeamSection() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [members, setMembers] = useState<DbTeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMembers() {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });
      if (!error && data) {
        setMembers(data as DbTeamMember[]);
      }
      setLoading(false);
    }
    fetchMembers();
  }, []);

  const filtered =
    activeCategory === "all"
      ? members
      : members.filter((t) => t.category === activeCategory);

  if (loading) {
    return (
      <section className="py-24 md:py-32 bg-white">
        <div className="px-6 md:px-10 lg:px-16 text-center">
          <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
          <p className="text-sm text-silver-400">載入中...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Leadership Team
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
              理監事與顧問團隊
            </h2>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-12 md:mb-16">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat.key
                  ? "bg-navy-950 text-white"
                  : "bg-silver-50 text-silver-600 hover:bg-silver-100"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Team grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {filtered.map((member) => (
            <div
              key={member.id}
              className="group bg-silver-50 rounded-3xl overflow-hidden border border-silver-100 hover:border-navy-200 hover:shadow-xl transition-all duration-500"
            >
              {/* Photo */}
              <div className="relative aspect-[4/5] overflow-hidden bg-navy-100">
                <SmartAvatar
                  src={member.image || ""}
                  name={member.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              {/* Info */}
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium bg-navy-950 text-white px-2.5 py-0.5 rounded-full">
                    {member.title}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-navy-950 mb-1">
                  {member.name}
                </h3>
                <p className="text-xs text-silver-500 mb-3">{member.organization}</p>
                <p className="text-sm text-silver-500 leading-relaxed line-clamp-3">
                  {member.bio || ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}